import { useState, useEffect } from 'react';
import { Routes, useLocation, Route, Navigate } from 'react-router-dom';
import { Header, Spinner } from '@weareopenr/component-library';
import AdminAppSidebar from 'src/components/AdminAppSidebar';
import styles from './Layout.module.scss';
import { Dashboard } from '../../pages/Dashboard/Dashboard';
import { Brand } from 'src/pages/Brand/Brand';
import { Users } from 'src/pages/Users/Users';
import { Roles } from 'src/pages/Roles/Roles';
import { User } from 'src/pages/User/User';
import { useAuthWrapper } from '@weareopenr/authkit';
import { usePermissions, useUserTracking } from 'src/hooks';
import ProtectedRoute from 'src/components/ProtectedRoute';

const Layout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, signOut, isLoading } = useAuthWrapper();
  const { canViewUsersAndRoles, canViewRoles } = usePermissions();
  const { identifyUser } = useUserTracking();

  useEffect(() => {
    if (user?.id) {
      identifyUser(user.id, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      });
    }
  }, [user, identifyUser]);

  const handleDrawerToggle = () => setIsSidebarOpen((prev: boolean) => !prev);
  const closeSideBar = () => setIsSidebarOpen(false);

  if (isLoading)
    return (
      <div className={styles.spinner}>
        <Spinner size="xlarge" colour="rainbow" />
      </div>
    );

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const displayName =
    user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email;

  return (
    <div data-testid="layout-root" className={styles['layout']}>
      <AdminAppSidebar
        open={isSidebarOpen}
        appName="Admin"
        currentPath={pathname}
        onSidebarClick={closeSideBar}
      />
      <div className={styles['right-container']}>
        <Header
          name={displayName}
          userRole="admin"
          switchAppName="Hub"
          onSwitchApp={() => {
            globalThis.location.href = globalThis.location.origin.replace('admin', 'hub');
          }}
          isAdminButtonEnabled
          onDrawerToggle={handleDrawerToggle}
          onLogout={signOut}
        />
        <div className={styles['content']}>
          <div className={styles['inner-content']}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/brands/:brandId" element={<Brand />} />
              <Route
                path="/users"
                element={
                  <ProtectedRoute element={<Users />} hasPermission={canViewUsersAndRoles} />
                }
              />
              <Route
                path="/users/:userId"
                element={<ProtectedRoute element={<User />} hasPermission={canViewUsersAndRoles} />}
              />
              <Route
                path="/roles"
                element={<ProtectedRoute element={<Roles />} hasPermission={canViewRoles} />}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
